import { useQuery } from '@apollo/client';
import { useAuth } from '../../auth/hooks/useAuth';
import {
  GET_MY_SUBSCRIPTION_QUERY,
  GET_MY_SUBSCRIPTION_PERMISSIONS_QUERY,
  GET_MY_PAYMENT_METHODS_QUERY
} from '../api/mutations';

export interface UseSubscriptionData {
  subscription: {
    planId: string;
    plan?: {
      id: string;
      name: string;
      description: string;
      planType?: string | null;
      amount: number;
      currency: string;
      features: string[];
      interval: string;
    };
    status: string;
    expiresAt: string | null;
    startedAt: string;
    isActive: boolean;
  // scheduled cancellation removed; use `status` to determine cancellation
    isPremium: boolean;
    clientSecret?: string | null;
    requiresAction: boolean;
    pendingActionType?: string | null;
  } | null;
  permissions: {
    canCreatePaidEvents: boolean;
    canSeeEventParticipants: boolean;
    canBringGuests: boolean;
    canSendAutomaticEmails: boolean;
    maxEventsPerMonth: number;
    adsHidden: boolean;
    // Effective number of guests this user may add to an event subscription: the plan's
    // allowance already clamped by the platform-wide setting, and 0 when canBringGuests
    // is false. Drive the guest picker off this and hide the block entirely when it is 0.
    maxGuests: number;
  } | null;
  paymentMethods: Array<{
    id: string;
    type: string;
    customerId: string;
    isDefault: boolean;
    card?: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
  }>;
  loading: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Hook to fetch subscription data using the new dedicated subscription endpoints.
 * This provides the same data as the user.subscription and user.subscriptionPermissions
 * but fetched from the new subscription service for better separation of concerns.
 */
export const useSubscriptionData = (): UseSubscriptionData => {
  const { isAuthenticated } = useAuth();
  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription
  } = useQuery(GET_MY_SUBSCRIPTION_QUERY, {
    errorPolicy: 'all',
    skip: !isAuthenticated
  });

  const {
    data: permissionsData,
    loading: permissionsLoading,
    error: permissionsError,
    refetch: refetchPermissions
  } = useQuery(GET_MY_SUBSCRIPTION_PERMISSIONS_QUERY, {
  errorPolicy: 'all',
  skip: !isAuthenticated
  });

  const {
    data: paymentMethodsData,
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods
  } = useQuery(GET_MY_PAYMENT_METHODS_QUERY, {
  errorPolicy: 'all',
  skip: !isAuthenticated
  });

  const loading = subscriptionLoading || permissionsLoading || paymentMethodsLoading;
  const error = subscriptionError || permissionsError || paymentMethodsError;

  const refetch = () => {
    refetchSubscription();
    refetchPermissions();
    refetchPaymentMethods();
  };

  return {
    subscription: subscriptionData?.mySubscription || null,
    permissions: permissionsData?.mySubscriptionPermissions || null,
    paymentMethods: paymentMethodsData?.myPaymentMethods || [],
    loading,
    error,
    refetch
  };
};

/**
 * Hook to fetch only subscription data (without permissions or payment methods)
 * Useful when you only need basic subscription information
 */
export const useSubscription = () => {
  const { isAuthenticated } = useAuth();
  const {
    data: subscriptionData,
    loading,
    error,
    refetch
  } = useQuery(GET_MY_SUBSCRIPTION_QUERY, {
    errorPolicy: 'all',
    skip: !isAuthenticated
  });

  return {
    subscription: subscriptionData?.mySubscription || null,
    loading,
    error,
    refetch
  };
};

/**
 * Hook to fetch only subscription permissions
 * Useful for authorization checks
 */
export const useSubscriptionPermissions = () => {
  const { isAuthenticated } = useAuth();

  // Dev override: if a page sets window.__mock_subscription_permissions, use it
  // This is useful for local debugging and reproducing permission-related flows
  // without hitting the backend. We still call the hook (skipped) to keep hook
  // ordering stable.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mock = (typeof window !== 'undefined' && (window as any).__mock_subscription_permissions) || null;

  const {
    data: permissionsData,
    loading,
    error,
    refetch
  } = useQuery(GET_MY_SUBSCRIPTION_PERMISSIONS_QUERY, {
    errorPolicy: 'all',
    // Skip the real network request when not authenticated or when mock is present
    skip: !!mock || !isAuthenticated
  });

  if (mock) {
    return {
      permissions: mock,
      loading: false,
      error: null,
      refetch: () => {},
    } as any;
  }

  return {
    permissions: permissionsData?.mySubscriptionPermissions || null,
    loading,
    error,
    refetch
  };
};

/**
 * Hook to fetch only payment methods
 * Useful for payment-related components
 */
export const usePaymentMethods = () => {
  const { isAuthenticated } = useAuth();
  const {
    data: paymentMethodsData,
    loading,
    error,
    refetch
  } = useQuery(GET_MY_PAYMENT_METHODS_QUERY, {
    errorPolicy: 'all',
    skip: !isAuthenticated
  });

  return {
    paymentMethods: paymentMethodsData?.myPaymentMethods || [],
    loading,
    error,
    refetch
  };
};
